import type { WritableDraft } from 'immer';

import {
  FieldValidationLevel,
  toBigWithDefault,
  validatePrecision,
  type FieldValidationRule,
  type NumericField,
} from '@talos/kyoko';
import { UNITS_CURRENCY, type CareOrderState } from './types';

export const quantityValidation: FieldValidationRule<NumericField, WritableDraft<CareOrderState>> = (
  field,
  context
) => {
  if (!context) {
    return null;
  }

  const { form, careOrderBeingModified } = context;

  const security = form?.symbolField.value;
  if (!security) {
    return null;
  }

  const quantity = toBigWithDefault(field.value, 0);

  const { MinimumSize, MinSizeIncrement } = security;
  const isInUnits = form.careOrderCurrencyField.value === UNITS_CURRENCY;

  const minSize = isInUnits ? 1 : MinimumSize;
  const minIncr = isInUnits ? 1 : MinSizeIncrement;

  if (quantity.lt(minSize)) {
    return {
      message: `Min quantity is ${minSize}`,
      level: FieldValidationLevel.Error,
    };
  }

  if (!validatePrecision(minIncr, field.value)) {
    return {
      message: `Min increment is ${minIncr}`,
      level: FieldValidationLevel.Error,
    };
  }

  if (careOrderBeingModified == null) {
    return null;
  }

  const careOrderQuantity = isInUnits
    ? careOrderBeingModified.basketOrderDetails?.BasketOrderQty
    : careOrderBeingModified.OrderQty;

  const workingQty = toBigWithDefault(careOrderBeingModified.WorkingQty, 0);
  if (workingQty.gt(0) && quantity.lt(toBigWithDefault(careOrderQuantity, 0))) {
    return {
      message: `Quantity cannot be decreased while there are open children.`,
      level: FieldValidationLevel.Error,
    };
  }

  // Below we check that the quantity modification desired doesn't result in a state where the OrderQty < CumQty (already executed quantity)
  const basketRatio = careOrderBeingModified.basketOrderDetails?.BasketRatio;
  if (isInUnits && basketRatio != null) {
    // For the unit case, we need to also do multiplication with the BasketRatio to figure this out
    const filledQty = toBigWithDefault(careOrderBeingModified.CumQty, 0);
    const quantityInBase = quantity.times(toBigWithDefault(basketRatio, 0));
    if (quantityInBase.lt(filledQty)) {
      return {
        message: `Token qty (${quantityInBase}) cannot be less than executed qty (${filledQty})`,
        level: FieldValidationLevel.Error,
      };
    }
  }

  if (!isInUnits) {
    const filledQty = toBigWithDefault(careOrderBeingModified.CumQty, 0);
    if (quantity.lt(filledQty)) {
      return {
        message: `Quantity cannot be less than already executed quantity: ${filledQty}`,
        level: FieldValidationLevel.Error,
      };
    }
  }

  return null;
};
